import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, SectionTag } from "../components/Reveal";
import SEO from "../components/SEO";
import { OUTCOME_BUCKETS } from "../content";

const DETAIL = {
  attract: {
    body: "Brand strategy, identity, packaging and communication that close the gap between how good your product is and how good it looks. We position manufacturers to be chosen on value, not compared on price.",
    deliverables: ["Brand strategy & positioning", "Identity systems & packaging", "Corporate & product communication", "Sales & marketing collateral"],
  },
  engage: {
    body: "Photoreal 3D product renders, AR experiences, interactive product explorers and Virtual Plant Visits. Buyers understand the product, and trust the factory behind it, long before the first meeting.",
    deliverables: ["Photoreal 3D product rendering", "AR product experiences", "Interactive product explorers", "Virtual Plant Visits (VPV)"],
  },
  convert: {
    body: "Exhibition environments, websites and campaigns engineered around a single outcome: a serious business enquiry. Every touchpoint carries the same brand, so interest never leaks between stall and screen.",
    deliverables: ["Exhibition & stall design", "Website design & development", "Campaign & invite design", "Digital presence & content"],
  },
};

export default function WhatWeDo() {
  return (
    <div data-testid="what-we-do-page">
      <SEO
        title="What We Do, Attract. Engage. Convert. | The Next Design"
        description="Branding & communication, 3D/AR/interactive product experiences, and exhibitions & digital, three outcomes The Next Design delivers for manufacturers."
      />
      <section className="border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <SectionTag>What We Do</SectionTag>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-light uppercase tracking-tight leading-none" data-testid="what-we-do-page-heading">
              Not services.<br /><span className="text-brand-red">Outcomes.</span>
            </h1>
            <p className="mt-8 text-lg text-neutral-600 max-w-2xl leading-relaxed">
              Everything we do serves one of three business outcomes, attract better buyers, engage them deeply, convert their interest into enquiry.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 space-y-px" data-testid="buckets-list">
        {OUTCOME_BUCKETS.map((b, i) => (
          <Reveal key={b.key} delay={i * 0.05}>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 border border-neutral-200 bg-white p-10 md:p-14" data-testid={`what-we-do-bucket-${b.key}`}>
              <div className="lg:col-span-4">
                <span className="font-display text-6xl font-semibold text-neutral-200">0{i + 1}</span>
                <h2 className="mt-4 font-display text-4xl font-semibold uppercase tracking-tight">
                  <span className="text-brand-red">{b.title}</span>
                </h2>
                <p className="mt-1 text-sm font-bold uppercase tracking-widest text-neutral-500">{b.subtitle}</p>
              </div>
              <div className="lg:col-span-5">
                <p className="text-neutral-600 leading-relaxed text-lg">{DETAIL[b.key].body}</p>
              </div>
              <div className="lg:col-span-3">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-neutral-500 mb-4">Deliverables</p>
                <ul className="space-y-3">
                  {DETAIL[b.key].deliverables.map((d) => (
                    <li key={d} className="text-sm text-neutral-900 flex items-start gap-2">
                      <span className="w-1.5 h-1.5 bg-brand-red inline-block mt-1.5 shrink-0" /> {d}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="border-t border-neutral-200 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight" data-testid="what-we-do-cta-heading">
            Not sure which outcome you need first?
          </h2>
          <Link
            to="/contact"
            data-testid="what-we-do-cta-button"
            className="bg-brand-red hover:bg-brand-red-dark text-white px-8 py-4 font-bold uppercase tracking-widest text-sm transition-colors inline-flex items-center gap-2 shrink-0"
          >
            Talk to Nitin <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
